import React, { useState } from 'react';
import { Download, X } from 'lucide-react';
import { usePwaInstall } from './usePwaInstall';
import { isPwaEnabled } from './pwaConfig';

/**
 * Floating "Install app" pill. Renders only when the browser reports the app
 * is installable (and not already installed), and can be dismissed for the
 * session. Fixed to the bottom-left so it stays clear of the chat composer and
 * the right-hand panels.
 *
 * Also gated by the PWA flag (VITE_PWA_ENABLED). When the PWA is disabled the
 * service worker isn't registered so `canInstall` would stay false anyway, but
 * we short-circuit here too so the button is unconditionally hidden.
 */
export function InstallAppButton(): JSX.Element | null {
  const { canInstall, promptInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState(false);

  if (!isPwaEnabled() || !canInstall || dismissed) return null;

  return (
    <div className="fixed bottom-4 left-4 z-[60] flex items-center gap-1 rounded-full border border-gray-200 bg-white pl-1 pr-2 py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
      <button
        type="button"
        onClick={promptInstall}
        className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
        data-testid="pwa-install-button"
      >
        <Download className="h-4 w-4 text-primary-600" strokeWidth={2} />
        Install app
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss install prompt"
        className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
      >
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}

export default InstallAppButton;
